// LT-312 — Static + dynamic sources for the Cmd-K palette. Three groups
// (in the display order the palette shows them in):
//
//   1. Navigation — the 6 top-level Workspace destinations (matching the
//      LT-311 Sidebar rewrite) plus every /settings/<tab> sub-route. Order
//      is the Sidebar order followed by Settings-hub tab order so the
//      mental model matches the rail.
//   2. Recent — the last 10 visited pages, persisted in localStorage
//      (no backend). RouteTracker in App.tsx writes; the palette reads.
//   3. Actions — a hard-coded registry of quick actions the user can
//      invoke directly from the palette (navigate to a page and trigger
//      a creation flow via URL query params, or no-op for future
//      Phase 2).

import {
  BarChart3,
  Building2,
  CreditCard,
  FileText,
  Flag,
  Layers,
  Mail,
  Megaphone,
  PhoneCall,
  Plug,
  Plus,
  Rocket,
  ShieldCheck,
  Settings as SettingsIcon,
  TrendingUp,
  User,
  Users,
  Variable,
  Calendar as CalendarIcon,
  CalendarCheck,
  PhoneIncoming,
  Mail as MailIcon,
  UserPlus,
} from 'lucide-react';

export type IconComponent = React.ComponentType<{ className?: string }>;

export interface NavItem {
  id: string;
  label: string;
  to: string;
  keywords?: string[];
  icon: IconComponent;
}

export interface ActionItem {
  id: string;
  label: string;
  to: string;
  keywords?: string[];
  icon: IconComponent;
}

// 6 Workspace destinations + Settings hub + Follow-ups (the sidebar's
// Attention group). Then every Settings sub-route so users can jump
// directly into a tab instead of landing on the overview.
export const NAV_ITEMS: NavItem[] = [
  { id: 'nav.dashboard', label: 'Dashboard', to: '/dashboard', icon: BarChart3, keywords: ['home', 'overview'] },
  // LT-532: Sequences retired. Campaigns is the operator-facing surface.
  { id: 'nav.campaigns', label: 'Campaigns', to: '/campaigns', icon: Megaphone, keywords: ['sequences', 'runs', 'initiatives'] },
  { id: 'nav.contacts', label: 'Contacts', to: '/contacts', icon: Users, keywords: ['leads', 'people'] },
  { id: 'nav.calls', label: 'Calls', to: '/calls', icon: PhoneCall, keywords: ['transcripts'] },
  { id: 'nav.analytics', label: 'Analytics', to: '/analytics', icon: TrendingUp, keywords: ['reports', 'stats'] },
  { id: 'nav.settings', label: 'Settings', to: '/settings', icon: SettingsIcon, keywords: ['preferences', 'config'] },
  { id: 'nav.follow_ups', label: 'Follow-ups', to: '/follow-ups', icon: Flag, keywords: ['tasks', 'attention'] },

  // Settings sub-routes — lowest-key first (matches hub tab nav order).
  { id: 'nav.settings.account', label: 'Settings — Account', to: '/settings/account', icon: User, keywords: ['profile', 'email', 'phone'] },
  { id: 'nav.settings.team', label: 'Settings — Team', to: '/settings/team', icon: Users, keywords: ['members', 'invites', 'roles'] },
  { id: 'nav.settings.templates', label: 'Settings — Templates', to: '/settings/templates', icon: FileText, keywords: ['email templates', 'sms templates'] },
  { id: 'nav.settings.custom_fields', label: 'Settings — Custom Fields', to: '/settings/custom-fields', icon: Variable, keywords: ['variables', 'contact schema'] },
  { id: 'nav.settings.booking_pages', label: 'Settings — Booking Pages', to: '/settings/booking-pages', icon: CalendarCheck, keywords: ['calendly'] },
  { id: 'nav.settings.calendars', label: 'Settings — Calendars', to: '/settings/calendars', icon: CalendarIcon, keywords: ['google', 'outlook'] },
  { id: 'nav.settings.integrations', label: 'Settings — Integrations', to: '/settings/integrations', icon: Plug, keywords: ['crm', 'webhooks'] },
  { id: 'nav.settings.senders', label: 'Settings — Senders', to: '/settings/senders', icon: MailIcon, keywords: ['verified', 'from addresses'] },
  { id: 'nav.settings.email_deliverability', label: 'Settings — Email Deliverability', to: '/settings/email-deliverability', icon: ShieldCheck, keywords: ['spf', 'dkim'] },
  { id: 'nav.settings.inbound_config', label: 'Settings — Inbound Config', to: '/settings/inbound-config', icon: PhoneIncoming, keywords: ['ivr', 'routing'] },
  { id: 'nav.settings.pipelines', label: 'Settings — Pipelines', to: '/settings/pipelines', icon: Layers, keywords: ['stages'] },
  { id: 'nav.settings.billing', label: 'Settings — Billing', to: '/settings/billing', icon: CreditCard, keywords: ['subscription', 'credits', 'plan'] },

  // Non-sidebar but commonly targeted destinations (sidebar-dropped
  // per LT-311 but still real pages).
  { id: 'nav.accounts', label: 'Accounts', to: '/accounts', icon: Building2, keywords: ['companies'] },
  { id: 'nav.deals', label: 'Deals', to: '/deals', icon: Mail, keywords: ['opportunities', 'pipeline'] },
];

// At least 5 actions per AC. All are "jump to X and trigger Y" flows —
// the target page picks up the intent via URL. Creation wizards that
// don't yet honour a ?action= param will simply land on the list; the
// palette contract is "get me there fast", not "do it remotely".
export const ACTIONS: ActionItem[] = [
  // LT-532: "Create new sequence" removed — the builder surface is retired.
  { id: 'action.new_campaign', label: 'Create new campaign', to: '/campaigns/create', icon: Plus, keywords: ['campaign', 'sequence', 'new'] },
  { id: 'action.new_contact', label: 'Add a contact', to: '/contacts?new=1', icon: UserPlus, keywords: ['contact', 'lead'] },
  { id: 'action.import_contacts', label: 'Import contacts from CSV', to: '/contacts?upload=1', icon: Rocket, keywords: ['csv', 'bulk', 'upload'] },
  { id: 'action.invite_teammate', label: 'Invite a teammate', to: '/settings/team', icon: UserPlus, keywords: ['invite', 'member', 'role'] },
  { id: 'action.new_account', label: 'Add an account', to: '/accounts?new=1', icon: Building2, keywords: ['company', 'new'] },
  { id: 'action.new_deal', label: 'Add a deal', to: '/deals?new=1', icon: Mail, keywords: ['opportunity', 'pipeline', 'new'] },
];

export const RECENT_STORAGE_KEY = 'commandPalette:recent:v1';
export const RECENT_LIMIT = 10;

export interface RecentEntry {
  path: string;
  label: string;
  visitedAt: number;
}

export function readRecentPages(): RecentEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(RECENT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as RecentEntry[];
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, RECENT_LIMIT);
  } catch {
    return [];
  }
}

/**
 * Push a visit to the palette's recent list. De-duplicates by path (a
 * revisit bumps the existing entry to the top). Capped at RECENT_LIMIT.
 * Called from App.tsx's RouteTracker on every pathname change.
 */
export function recordRecentVisit(path: string, label: string): void {
  if (typeof window === 'undefined') return;
  // Skip synthetic / auth-adjacent paths. We don't want the palette to
  // recommend "Login" or an arbitrary wizard step.
  if (
    path.startsWith('/login') ||
    path.startsWith('/signup') ||
    path.startsWith('/reset-password') ||
    path === '/' ||
    path === '/dashboard'
  ) {
    return;
  }
  try {
    const existing = readRecentPages().filter((r) => r.path !== path);
    const next: RecentEntry[] = [
      { path, label, visitedAt: Date.now() },
      ...existing,
    ].slice(0, RECENT_LIMIT);
    window.localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* noop: localStorage disabled / full */
  }
}

/**
 * Best-effort label extractor for a pathname — uses the NAV_ITEMS map
 * first, then falls back to pretty-casing the last segment.
 */
export function labelForPath(path: string): string {
  const nav = NAV_ITEMS.find((n) => n.to === path);
  if (nav) return nav.label;
  const seg = path.split('/').filter(Boolean).pop() ?? path;
  return seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
